import { action, makeAutoObservable, runInAction } from 'mobx'

// evertel
import { injectable, inject, decorate } from '@evertel/di'
import { Api } from '@evertel/api'
import { APIDataRoomMessage, APIDataThreadMessage } from '@evertel/types'
import { DisplayRoomMessage, DisplayThreadMessage, RoomMessagesStore, ThreadMessagesStore } from '@evertel/stores'
import { APIDataMediaToNormalizedMedia, MediaUpload, MediaUploadService, NormalizedMedia } from '@evertel/media'

class MessageController {
    //typing the {} is the best way to not use Partial<> and let it use {} as default until init
    message: DisplayRoomMessage | DisplayThreadMessage = {} as DisplayRoomMessage | DisplayThreadMessage
    modelType: 'room' | 'thread' = 'room'
    extraMedia: NormalizedMedia[] = []

    constructor(
        private api: Api,
        private roomMessagesStore: RoomMessagesStore,
        private threadMessagesStore: ThreadMessagesStore,
        private mediaUploadService: MediaUploadService,
        private platform: 'mobile' | 'web'

    ) {
        makeAutoObservable(this, {init: action})
    }

    init = (
        message: DisplayRoomMessage | DisplayThreadMessage,
        modelType: 'room' | 'thread',
        extraMedia?: NormalizedMedia[]
    ) => {
        if (!message) console.error('Missing message! in MessageController.init')

        this.message = message
        this.extraMedia = extraMedia || [] // this includes media parsed from links in the message
        this.modelType = modelType

    }

    get uploadableMedia() {
        // grab uploadable media for this message
        return this.mediaUploadService.getMediaUploadsForModel(this.message.id, this.modelType)
    }

    startUploadMedia = async () => {
        // TODO: should this be elsewhere?
        await this.mediaUploadService.startMediaUploads(this.uploadableMedia.filter(m => m.state === 'readyToUpload'), this.onUploadComplete)
    }

    retryUploadMedia = async () => {
        await this.mediaUploadService.startMediaUploads(this.uploadableMedia.filter(m => m.state === 'failed'), this.onUploadComplete)
    }

    retract = async () => {
        const retractedMessage = this.modelType === 'room'
            ? await this.api.Routes.RoomMessage.putRetract(this.message.id)
            : await this.api.Routes.ThreadMessage.putRetract(this.message.id)

        runInAction(() => {
            // New fetched messages are based on the updated date.
            // Including an updated date will break message fetch order
            retractedMessage.updatedDate = undefined

            if ((this.modelType === 'room')) {
                this.roomMessagesStore.update(retractedMessage)
            } else {
                this.threadMessagesStore.update(retractedMessage)
            }
        })

        //log to analytics
        // AnalyticsStore.logEvent({
        //     category: 'User',
        //     action: 'retract_room_message'
        // })
    }

    // This is used by UploadableMediaState.startUpload(). When any media upload completes check if we should publish the message.
    private onUploadComplete = async (mediaUploads: MediaUpload[]) => {
        // TODO: maybe pay attention to 'error' ?
        if (mediaUploads.every((m: { state: string }) => m.state === 'complete')) {
            if (!this.message.publishedDate) {
                // Publish this message
                const publishedMessage = this.modelType === 'room'
                    ? await this.api.Routes.RoomMessage.putPublish(this.message.id) as APIDataRoomMessage
                    : await this.api.Routes.ThreadMessage.putPublish(this.message.id) as APIDataThreadMessage

                // New fetched messages are based on the updated date.
                // Including an updated date will break message fetch order
                publishedMessage.updatedDate = undefined

                // Update the message in the store
                if ((this.modelType === 'room')) {
                    this.roomMessagesStore.update(publishedMessage)
                } else {
                    this.threadMessagesStore.update(publishedMessage)
                }
            }
        }
    }

    get mediaUploadProgress() {
        const uploadingMedia = this.uploadableMedia.filter(m => m.state === 'uploading')
        const uploadProgress = uploadingMedia.reduce((r, m) => r + m.uploadProgress, 0)
        return (uploadingMedia.length > 0 && uploadProgress > 0) ? (uploadingMedia.length / uploadProgress) : 0
    }

    get filter() {
        return {
            where: {}
        }
    }

    get media(): NormalizedMedia[] {
        const heicSupport = this.platform === 'mobile'
        const remoteMedia = this.message.media?.map((m) => APIDataMediaToNormalizedMedia(m, heicSupport)) || []

        const filterRemoteMedia = remoteMedia.filter(m => !this.uploadableMedia.some(um => um.remoteMedia?.id === m.id))

        return [...filterRemoteMedia, ...(this.uploadableMedia?.length ? this.uploadableMedia : []), ...this.extraMedia]
    }

}

let i = 0
decorate(injectable(), MessageController)
decorate(inject(Api), MessageController, i++)
decorate(inject(RoomMessagesStore), MessageController, i++)
decorate(inject(ThreadMessagesStore), MessageController, i++)
decorate(inject(MediaUploadService), MessageController, i++)
decorate(inject('Platform' as any), MessageController, i++)

export { MessageController }
